import { serviceClient,unwrap } from './client';
import type { RequestConfig } from './types';

// The calls a module makes, checked against the service's OpenAPI document (see scripts/openapi.mjs).
//
//   const sign = service<paths>(API_ROOT);
//   export const contactApi = endpoints({
//     query: sign.get('/v1/sign/contacts'),
//     update: sign.post('/v1/sign/contacts/{uuid}'),
//   });
//   contactApi('update', { uuid, name: 'Ada' })
//
// The path must be one the document has for that HTTP method. The call takes one object: the fields the address names go
// in the address, and the rest is the query of a GET or the body of anything else. What it answers is the document's answer.
// A module that declares its own payload and answer types (its `AllMethods`) binds the endpoints to them with `endpoints<AllMethods>`,
// and the compiler then refuses any call the document does not match.

type Operation = { parameters?: unknown; requestBody?: unknown; responses?: unknown };
type Verb = 'get' | 'post' | 'delete';

type PathsWith<Paths, V extends Verb> = {
  [P in keyof Paths & string]: Paths[P] extends Record<V, Operation> ? P : never;
}[keyof Paths & string];

type OperationOf<Paths, P extends keyof Paths, V extends Verb> = Paths[P] extends Record<V, infer O> ? O : never;

type JsonContent<T> = T extends { content: { 'application/json': infer B } } ? B : never;
type Flat<T> = { [K in keyof T]: T[K] } & {};

type Obj<T> = [T] extends [never] ? {} : [T] extends [object] ? T : {};
type PathPart<O> = O extends { parameters: { path: infer X } } ? Obj<X> : {};
type QueryPart<O> = O extends { parameters: { query?: infer Q } } ? Obj<NonNullable<Q>> : {};
type BodyPart<O> = O extends { requestBody?: infer B } ? Obj<JsonContent<NonNullable<B>>> : {};

export type Payload<O> = Flat<PathPart<O> & QueryPart<O> & BodyPart<O>>;

type Responses<O> = O extends { responses: infer R } ? R : {};
type Answer<R> = R extends { 200: infer X } ? JsonContent<X>
  : R extends { 201: infer X } ? JsonContent<X>
  : R extends { 202: infer X } ? JsonContent<X>
  : null;
export type Success<O> = [Answer<Responses<O>>] extends [never] ? null : Answer<Responses<O>>;

// Callers that name a route parameter differently from the document (form_uuid for uuid) say so: { form_uuid: 'uuid' }.
type Aliases = Record<string, string>;
type Renamed<P, A extends Aliases> = string extends keyof A ? P : [keyof A] extends [never] ? P
  : Flat<Omit<P, A[keyof A] & keyof P> & { [K in keyof A]: A[K] extends keyof P ? P[A[K]] : never }>;

export type Endpoint<O, A extends Aliases = {}> = (payload: Renamed<Payload<O>, A>, config?: RequestConfig) => Promise<{ data: Success<O>; status: number }>;

// What every app does with an answer before its caller sees it (a 401 resets the session, errors are mapped to messages, files are
// fetched); each app sets it once when it starts. It gets the promise of the call and the config the call was made with.
type ResponseStep = (promise: Promise<any>, request?: { config?: RequestConfig }) => Promise<any>;
let responseSteps: readonly ResponseStep[] = [];
export const setResponseMiddleware = (steps: readonly ResponseStep[]) => {
  responseSteps = steps;
};

const placeholders = (path: string) => [...path.matchAll(/\{(\w+)\}/g)].map(match => match[1]);

const call = (client: () => any, verb: Verb, path: string, aliases: Aliases = {}) => (payload: Record<string, unknown> = {}, config?: RequestConfig) => {
  const names = placeholders(path);
  const pathValues: Record<string, unknown> = {};
  const rest: Record<string, unknown> = { ...payload };
  Object.entries(aliases).forEach(([from, to]) => {
    if (from in rest) { rest[to] = rest[from]; delete rest[from]; }
  });
  names.forEach(name => {
    if (rest[name] === undefined || rest[name] === null || rest[name] === '') throw new Error(`Missing route parameter ${name} for ${path}`);
    pathValues[name] = rest[name];
    delete rest[name];
  });
  const headers = config ? Object.fromEntries(Object.entries(config).filter(([, value]) => value !== undefined).map(([name, value]) => [name, String(value)])) : undefined;
  const base = { params: { path: pathValues, query: verb === 'get' ? rest : undefined }, headers };
  const request = verb === 'get'
    ? client().GET(path, base)
    : client()[verb === 'post' ? 'POST' : 'DELETE'](path, verb === 'delete' && !Object.keys(rest).length ? base : { ...base, body: rest });
  return responseSteps.reduce((promise, step) => step(promise, { config }), unwrap(request) as Promise<any>);
};

// The builders for one service's document. `base` is where the service is reached; the paths of the document begin after it.
export const service = <Paths extends {}>(base: string | (() => string)) => {
  const client = serviceClient<Paths>(base);
  return {
    get: <P extends PathsWith<Paths, 'get'>, const A extends Aliases = {}>(path: P, aliases?: A) => call(client, 'get', path, aliases) as unknown as Endpoint<OperationOf<Paths, P, 'get'>, A>,
    post: <P extends PathsWith<Paths, 'post'>, const A extends Aliases = {}>(path: P, aliases?: A) => call(client, 'post', path, aliases) as unknown as Endpoint<OperationOf<Paths, P, 'post'>, A>,
    del: <P extends PathsWith<Paths, 'delete'>, const A extends Aliases = {}>(path: P, aliases?: A) => call(client, 'delete', path, aliases) as unknown as Endpoint<OperationOf<Paths, P, 'delete'>, A>,
  };
};

type Handlers = Record<string, (payload: any, config?: RequestConfig) => Promise<{ data: any; status: number }>>;

// Names a module's endpoints and gives them one door: api('update', payload).
//
// With a declared methods type (the module's AllMethods), each endpoint must accept what the method's payload holds, so a
// payload the document's route does not take is a compile error. The answer is typed as the module declares it: a document
// leaves a field optional that a model requires, because the service leaves a null out, so the answer is not compared.
export function endpoints<Methods extends Record<string, { payload: any; success: any }>>(
  handlers: { [K in keyof Methods]: (payload: Methods[K]['payload'], config?: RequestConfig) => Promise<{ data: any; status: number }> }
): <K extends keyof Methods>(name: K, payload: Methods[K]['payload'], config?: RequestConfig) => Promise<{ data: Methods[K]['success']; status: number }>;
export function endpoints<H extends Handlers>(
  handlers: H
): <K extends keyof H>(name: K, payload: Parameters<H[K]>[0], config?: RequestConfig) => ReturnType<H[K]>;
export function endpoints(handlers: Handlers) {
  return (name: string, payload: unknown, config?: RequestConfig) => handlers[name](payload, config);
}

// One call that is not part of a namespace: a function from the payload to the answer.
export const single = <M extends { payload: any; success: any }>(
  handler: (payload: M['payload'], config?: RequestConfig) => Promise<{ data: any; status: number }>
) => (payload: M['payload'], config?: RequestConfig): Promise<{ data: M['success']; status: number }> => handler(payload, config);

// For a call the service has no route for: it fails when it is made, saying which call it is.
export const unserved = (call: string) => (): never => {
  throw new Error(`The service has no route for ${call}`);
};

